import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Header } from '../components/Header';
import { PageHeader } from '../components/PageHeader';
import { PageLayout } from '../components/PageLayout';

interface ExpiringTenant {
  id: string;
  name: string;
  slug: string;
  expiresAt: string;
}

interface CircuitBreakerStatus {
  state: 'closed' | 'open' | 'half_open';
  openedAt: number | null;
  openBuckets?: string[];
}

interface RecentAlert {
  id: string;
  type: string;
  severity: string;
  title: string;
  message: string;
  resolved: boolean;
  createdAt: string;
  tenant?: { id: string; name: string; slug: string } | null;
}

interface SystemStats {
  tenants: {
    total: number;
    active: number;
    inactive: number;
    expiringToday: number;
    expiringIn7Days: number;
    expiredOverdue: number;
    expiringTodayList?: ExpiringTenant[];
    expiringIn7DaysList?: ExpiringTenant[];
    expiredOverdueList?: (ExpiringTenant & { active?: boolean })[];
  };
  users: {
    total: number;
    byRole: {
      SUPERADMIN: number;
      ADMIN: number;
      USER: number;
    };
  };
  resources: {
    totalContacts: number;
    totalCampaigns: number;
    totalSessions: number;
    totalMessages: number;
    messagesThisMonth?: number;
    totalBots: number;
    activeBots: number;
  };
  activity: {
    activeToday: number;
    activeCampaigns: number;
    workingSessions: number;
  };
  alerts?: {
    total: number;
    unresolved: number;
    critical: number;
    high: number;
  };
  circuitBreaker?: CircuitBreakerStatus;
}

interface WahaSessionsStats {
  total: number;
  working: number;
  failed: number;
  stopped: number;
  scanQr: number;
  failedSessionNames: string[];
  error?: string;
}

export function SuperAdminDashboard() {
  const [stats, setStats] = useState<SystemStats | null>(null);
  const [wahaStats, setWahaStats] = useState<WahaSessionsStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [forceClosingCircuit, setForceClosingCircuit] = useState(false);
  const [cleaningFailed, setCleaningFailed] = useState(false);
  const [recentAlerts, setRecentAlerts] = useState<RecentAlert[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    loadStats();
    loadRecentAlerts();

    const handleFocus = () => {
      loadStats();
      loadRecentAlerts();
    };
    window.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  const loadRecentAlerts = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch('/api/alerts?resolved=false&limit=10', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setRecentAlerts(data.alerts ?? []);
      }
    } catch {
      setRecentAlerts([]);
    }
  };

  const loadStats = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const [statsRes, wahaRes] = await Promise.all([
        fetch('/api/system/stats', { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch('/api/system/waha-sessions-stats', { headers: { 'Authorization': `Bearer ${token}` } })
      ]);

      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data);
      }
      if (wahaRes.ok) {
        const wahaData = await wahaRes.json();
        setWahaStats(wahaData);
      } else {
        setWahaStats({ total: 0, working: 0, failed: 0, stopped: 0, scanQr: 0, failedSessionNames: [], error: 'Erro ao carregar' });
      }
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
      toast.error('Erro ao carregar estatísticas');
    } finally {
      setLoading(false);
    }
  };

  const handleForceCloseCircuit = async () => {
    try {
      setForceClosingCircuit(true);
      const token = localStorage.getItem('auth_token');
      const response = await fetch('/api/system/circuit-breaker/force-close', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok && data.success) {
        toast.success('Circuito fechado. Campanhas podem ser reativadas pelos tenants.');
        await loadStats();
        await loadRecentAlerts();
      } else {
        toast.error(data.error || 'Erro ao fechar circuito');
      }
    } catch (error) {
      console.error('Erro ao forçar fechamento do circuito:', error);
      toast.error('Erro ao fechar circuito');
    } finally {
      setForceClosingCircuit(false);
    }
  };

  const handleCleanFailedSessions = async () => {
    if (!wahaStats?.failed || wahaStats.failed === 0) {
      toast('Nenhuma conexão falhada para remover.');
      return;
    }
    if (!confirm(`Remover ${wahaStats.failed} conexão(ões) falhada(s) da WAHA e do sistema? Elas serão excluídas permanentemente.`)) return;
    try {
      setCleaningFailed(true);
      const token = localStorage.getItem('auth_token');
      const response = await fetch('/api/system/waha/clean-failed-sessions', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok && data.success) {
        toast.success(`${data.removedCount} conexão(ões) falhada(s) removida(s).`);
        if (data.errors?.length) {
          toast.error(`Algumas falharam: ${data.errors.map((e: any) => e.name).join(', ')}`);
        }
        await loadStats();
      } else {
        toast.error(data.error || 'Erro ao remover conexões falhadas');
      }
    } catch (error) {
      console.error('Erro ao remover conexões falhadas:', error);
      toast.error('Erro ao remover conexões falhadas');
    } finally {
      setCleaningFailed(false);
    }
  };

  if (loading) {
    return (
      <div className="flex-1 flex flex-col h-screen">
        <Header title="Dashboard" subtitle="Visão geral do sistema" />
        <div className="flex-1 p-8">
          <div className="animate-pulse">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="h-28 rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-white/[0.03]"></div>
              ))}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-24 rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-white/[0.03]"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col h-screen">
      <Header
        title="Dashboard"
        subtitle="Visão geral do sistema"
        actions={
          <button
            onClick={loadStats}
            className="btn-header-refresh"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Atualizar
          </button>
        }
      />

      <div className="flex-1 overflow-auto bg-page">
        <PageLayout>
          <PageHeader
            title="Dashboard Super Admin"
            breadcrumbs={[
              { label: 'Super Admin', href: '/superadmin/dashboard' },
              { label: 'Dashboard' }
            ]}
          />
          <div className="space-y-8">
          {/* Overview principal — mesmo padrão da visão de conexões */}
          <div className="mb-6 rounded-2xl border border-gray-200 bg-white p-4 sm:p-6 dark:border-gray-800 dark:bg-white/[0.03]">
            <div className="mb-6 flex items-center justify-between">
              <h2 className="font-semibold text-gray-800 dark:text-white/90">Visão geral do sistema</h2>
              <button
                type="button"
                onClick={loadStats}
                className="btn-atualizar"
              >
                <i className="fas fa-arrows-rotate text-base shrink-0" aria-hidden />
                Atualizar
              </button>
            </div>
            <div className="grid grid-cols-1 rounded-xl border border-gray-200 sm:grid-cols-2 lg:grid-cols-5 lg:divide-x lg:divide-y-0 dark:divide-gray-800 dark:border-gray-800">
              <div className="border-b p-5 sm:border-r lg:border-b-0">
                <div className="mb-1.5 flex items-center gap-2">
                  <span className="text-blue-600 dark:text-blue-400 text-xl" aria-hidden>
                    <i className="fas fa-building" />
                  </span>
                  <p className="text-sm text-gray-400 dark:text-gray-500">Empresas ativas</p>
                </div>
                <h3 className="text-3xl text-gray-800 dark:text-white/90">
                  {stats?.tenants.active ?? 0}
                  <span className="ml-2 text-sm font-normal text-gray-400 dark:text-gray-500">
                    / {stats?.tenants.total ?? 0}
                  </span>
                </h3>
              </div>
              <div className="border-b p-5 lg:border-b-0">
                <div className="mb-1.5 flex items-center gap-2">
                  <span className="text-red-600 dark:text-red-400 text-xl" aria-hidden>
                    <i className="fas fa-triangle-exclamation" />
                  </span>
                  <p className="text-sm text-gray-400 dark:text-gray-500">Expiram hoje</p>
                </div>
                <h3 className="text-3xl text-gray-800 dark:text-white/90">
                  {stats?.tenants.expiringToday ?? 0}
                </h3>
              </div>
              <div className="border-b p-5 sm:border-r sm:border-b-0">
                <div className="mb-1.5 flex items-center gap-2">
                  <span className="text-amber-600 dark:text-amber-400 text-xl" aria-hidden>
                    <i className="fas fa-calendar-day" />
                  </span>
                  <p className="text-sm text-gray-400 dark:text-gray-500">Expiram em 7 dias</p>
                </div>
                <h3 className="text-3xl text-gray-800 dark:text-white/90">
                  {stats?.tenants.expiringIn7Days ?? 0}
                </h3>
              </div>
              <div className="border-b p-5 sm:border-r lg:border-b-0">
                <div className="mb-1.5 flex items-center gap-2">
                  <span className="text-emerald-600 dark:text-emerald-400 text-xl" aria-hidden>
                    <i className="fas fa-signal" />
                  </span>
                  <p className="text-sm text-gray-400 dark:text-gray-500">Conexões ativas</p>
                </div>
                <h3 className="text-3xl text-gray-800 dark:text-white/90">
                  {wahaStats?.error ? '—' : (wahaStats?.working ?? 0)}
                </h3>
                {wahaStats?.error && (
                  <p className="text-xs text-amber-600 dark:text-amber-400 mt-0.5">Fonte: WAHA</p>
                )}
              </div>
              <div className="p-5">
                <div className="mb-1.5 flex items-center gap-2">
                  <span className="text-red-600 dark:text-red-400 text-xl" aria-hidden>
                    <i className="fas fa-circle-xmark" />
                  </span>
                  <p className="text-sm text-gray-400 dark:text-gray-500">Conexões falhadas</p>
                </div>
                <h3 className="text-3xl text-gray-800 dark:text-white/90">
                  {wahaStats?.error ? '—' : (wahaStats?.failed ?? 0)}
                </h3>
                {(wahaStats?.failed ?? 0) > 0 && (
                  <button
                    type="button"
                    onClick={handleCleanFailedSessions}
                    disabled={cleaningFailed}
                    className="mt-2 text-xs font-medium text-red-600 dark:text-red-400 hover:underline disabled:opacity-50"
                  >
                    {cleaningFailed ? 'Removendo...' : 'Remover falhadas'}
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Segunda linha de métricas */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="card-padrao">
              <div className="flex items-center gap-4">
                <div className="bg-orange-100 dark:bg-orange-900/30 p-3 rounded-xl">
                  <svg className="w-6 h-6 text-orange-600 dark:text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Total de campanhas</p>
                  <p className="text-2xl font-bold text-gray-800 dark:text-white/90">{stats?.resources.totalCampaigns ?? 0}</p>
                  <p className="text-xs text-emerald-600 dark:text-emerald-400">{stats?.activity.activeCampaigns ?? 0} ativas agora</p>
                </div>
              </div>
            </div>

            <div className="card-padrao">
              <div className="flex items-center gap-4">
                <div className="bg-blue-100 dark:bg-blue-900/30 p-3 rounded-xl">
                  <svg className="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Mensagens enviadas</p>
                  <p className="text-2xl font-bold text-gray-800 dark:text-white/90">{(stats?.resources.messagesThisMonth ?? 0).toLocaleString('pt-BR')}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">este mês</p>
                </div>
              </div>
            </div>

            <div className="card-padrao">
              <div className="flex items-center gap-4">
                <div className="bg-emerald-100 dark:bg-emerald-900/30 p-3 rounded-xl">
                  <svg className="w-6 h-6 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Bots / automações</p>
                  <p className="text-2xl font-bold text-gray-800 dark:text-white/90">
                    {(stats?.resources.totalBots ?? 0).toLocaleString('pt-BR')}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {(stats?.resources.activeBots ?? 0).toLocaleString('pt-BR')} ativos
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Terceira linha - hoje e resumo em um card cheio, 2 colunas */}
          <div className="card-padrao">
            <div className="grid gap-8 lg:grid-cols-2">
              <div>
                <h3 className="text-base font-semibold text-gray-800 dark:text-white/90 mb-3">Hoje</h3>
                <div className="space-y-3 text-sm">
                  {stats?.circuitBreaker?.state === 'open' && (
                    <div className="flex items-center justify-between rounded-xl bg-red-50 dark:bg-red-900/20 px-3 py-2.5">
                      <div className="flex flex-col">
                        <span className="font-medium text-red-800 dark:text-red-200">
                          API WhatsApp em proteção (circuito aberto)
                          {(stats.circuitBreaker.openBuckets?.length ?? 0) > 0 && (
                            <span className="ml-1 font-normal text-red-700 dark:text-red-300">
                              — {stats.circuitBreaker.openBuckets!.length} tenant(s)
                            </span>
                          )}
                        </span>
                        <span className="text-xs text-red-700 dark:text-red-300">
                          Campanhas desses tenants foram pausadas. Podem reativar quando a API voltar ou force o fechamento abaixo.
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={handleForceCloseCircuit}
                        disabled={forceClosingCircuit}
                        className="rounded-lg bg-red-100 dark:bg-red-900/40 px-3 py-1.5 text-xs font-semibold text-red-800 dark:text-red-200 hover:bg-red-200 dark:hover:bg-red-900/60 disabled:opacity-50"
                      >
                        {forceClosingCircuit ? 'Fechando...' : 'Forçar fechamento'}
                      </button>
                    </div>
                  )}
                  {stats?.circuitBreaker?.state === 'half_open' && (
                    <div className="flex items-center justify-between rounded-xl bg-amber-50 dark:bg-amber-900/20 px-3 py-2.5">
                      <div className="flex flex-col">
                        <span className="font-medium text-amber-800 dark:text-amber-100">API WhatsApp em teste (half-open)</span>
                        <span className="text-xs text-amber-700 dark:text-amber-200">
                          Próxima requisição vai reabrir ou fechar o circuito.
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Vencidas: data de expiração já passou — prioridade máxima */}
                  {(stats?.tenants.expiredOverdue ?? 0) > 0 && (
                    <div className="flex items-center justify-between rounded-xl bg-red-50 dark:bg-red-900/20 px-3 py-2.5">
                      <div className="flex flex-col">
                        <span className="font-medium text-red-800 dark:text-red-200">Vencidas</span>
                        <span className="text-xs text-red-700 dark:text-red-300">
                          {stats?.tenants.expiredOverdue} empresa(s) com data de expiração já passada — renovar ou desativar
                        </span>
                      </div>
                      <span className="rounded-lg bg-red-100 dark:bg-red-900/40 px-2 py-1 text-xs font-semibold text-red-800 dark:text-red-200">
                        {stats?.tenants.expiredOverdue}
                      </span>
                    </div>
                  )}

                  {/* Expirando hoje */}
                  {(stats?.tenants.expiringToday ?? 0) > 0 && (
                    <div className="flex items-center justify-between rounded-xl bg-red-50 dark:bg-red-900/20 px-3 py-2.5">
                      <div className="flex flex-col">
                        <span className="font-medium text-red-800 dark:text-red-200">Expirando hoje</span>
                        <span className="text-xs text-red-700 dark:text-red-300">
                          {stats?.tenants.expiringToday} empresa(s) — renovar hoje para não vencer
                        </span>
                      </div>
                      <span className="rounded-lg bg-red-100 dark:bg-red-900/40 px-2 py-1 text-xs font-semibold text-red-800 dark:text-red-200">
                        {stats?.tenants.expiringToday}
                      </span>
                    </div>
                  )}

                  {/* Expiram em 7 dias */}
                  {(stats?.tenants.expiringIn7Days ?? 0) > 0 && (
                    <div className="flex items-center justify-between rounded-xl bg-amber-50 dark:bg-amber-900/20 px-3 py-2.5">
                      <div className="flex flex-col">
                        <span className="font-medium text-amber-800 dark:text-amber-100">Expiram em até 7 dias</span>
                        <span className="text-xs text-amber-700 dark:text-amber-200">
                          {stats?.tenants.expiringIn7Days} empresa(s) — acompanhar renovações
                        </span>
                      </div>
                      <span className="rounded-lg bg-amber-100 dark:bg-amber-900/40 px-2 py-1 text-xs font-semibold text-amber-800 dark:text-amber-200">
                        {stats?.tenants.expiringIn7Days}
                      </span>
                    </div>
                  )}

                  {/* Inativas */}
                  {(stats?.tenants.inactive ?? 0) > 0 && (
                    <div className="flex items-center justify-between rounded-xl bg-gray-100 dark:bg-white/[0.04] px-3 py-2.5">
                      <div className="flex flex-col">
                        <span className="font-medium text-gray-800 dark:text-gray-200">Inativas</span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {stats?.tenants.inactive} empresa(s) desativadas — revisar ou remover se não for mais usar
                        </span>
                      </div>
                      <span className="rounded-lg bg-gray-200 dark:bg-white/10 px-2 py-1 text-xs font-semibold text-gray-800 dark:text-gray-100">
                        {stats?.tenants.inactive}
                      </span>
                    </div>
                  )}

                  {stats?.circuitBreaker?.state !== 'open' &&
                    stats?.circuitBreaker?.state !== 'half_open' &&
                    (stats?.tenants.expiredOverdue ?? 0) === 0 &&
                    (stats?.tenants.expiringToday ?? 0) === 0 &&
                    (stats?.tenants.expiringIn7Days ?? 0) === 0 &&
                    (stats?.tenants.inactive ?? 0) === 0 && (
                      <p className="rounded-xl bg-gray-50 dark:bg-white/[0.03] px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                        Nenhuma notificação crítica para hoje.
                      </p>
                    )}
                </div>
              </div>

              <div>
                <h3 className="text-base font-semibold text-gray-800 dark:text-white/90 mb-3">Resumo do sistema</h3>
                <div className="divide-y divide-gray-200 dark:divide-gray-700 text-sm rounded-xl">
                  <div className="flex items-center justify-between px-4 py-2.5 first:rounded-t-xl">
                    <span className="text-gray-500 dark:text-gray-400">Empresas totais</span>
                    <span className="font-semibold text-gray-800 dark:text-white/90">
                      {stats?.tenants.total ?? 0}
                    </span>
                  </div>
                  <div className="flex items-center justify-between px-4 py-2.5">
                    <span className="text-gray-500 dark:text-gray-400">Usuários</span>
                    <span className="font-semibold text-gray-800 dark:text-white/90">
                      {stats?.users.total ?? 0}
                    </span>
                  </div>
                  <div className="flex items-center justify-between px-4 py-2.5">
                    <span className="text-gray-500 dark:text-gray-400">Administradores</span>
                    <span className="font-semibold text-gray-800 dark:text-white/90">
                      {(stats?.users.byRole?.ADMIN ?? 0) + (stats?.users.byRole?.SUPERADMIN ?? 0)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between px-4 py-2.5">
                    <span className="text-gray-500 dark:text-gray-400">Total de contatos</span>
                    <span className="font-semibold text-gray-800 dark:text-white/90">
                      {(stats?.resources.totalContacts ?? 0).toLocaleString('pt-BR')}
                    </span>
                  </div>
                  <div className="flex items-center justify-between px-4 py-2.5">
                    <span className="text-gray-500 dark:text-gray-400">Sessões WhatsApp</span>
                    <span className="font-semibold text-gray-800 dark:text-white/90">
                      {stats?.resources.totalSessions ?? 0}
                    </span>
                  </div>
                  <div className="flex items-center justify-between px-4 py-2.5">
                    <span className="text-gray-500 dark:text-gray-400">Usuários ativos hoje</span>
                    <span className="font-semibold text-emerald-600 dark:text-emerald-400">
                      {stats?.activity.activeToday ?? 0}
                    </span>
                  </div>
                  <div className="flex items-center justify-between px-4 py-2.5 last:rounded-b-xl">
                    <span className="text-gray-500 dark:text-gray-400">API WhatsApp (circuit breaker)</span>
                    <span className={`font-semibold ${
                      stats?.circuitBreaker?.state === 'open'
                        ? 'text-red-600 dark:text-red-400'
                        : stats?.circuitBreaker?.state === 'half_open'
                          ? 'text-amber-600 dark:text-amber-400'
                          : 'text-emerald-600 dark:text-emerald-400'
                    }`}>
                      {stats?.circuitBreaker?.state === 'open'
                        ? 'Aberto'
                        : stats?.circuitBreaker?.state === 'half_open'
                          ? 'Testando'
                          : 'OK'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Alertas recentes — aparecem aqui circuit breaker, quotas, etc. */}
          <div className="card-padrao">
            <h3 className="text-base font-semibold text-gray-800 dark:text-white/90 mb-3">Alertas recentes</h3>
            {recentAlerts.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400 py-2">Nenhum alerta em aberto.</p>
            ) : (
              <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                {recentAlerts.map((alert) => (
                  <li key={alert.id} className="py-3 first:pt-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-gray-800 dark:text-white/90 truncate">{alert.title}</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2 mt-0.5">{alert.message}</p>
                        <div className="flex items-center gap-2 mt-1 text-xs text-gray-400 dark:text-gray-500">
                          <span className={`font-medium ${
                            alert.severity === 'CRITICAL' ? 'text-red-600 dark:text-red-400' :
                            alert.severity === 'HIGH' ? 'text-amber-600 dark:text-amber-400' :
                            alert.severity === 'MEDIUM' ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500'
                          }`}>
                            {alert.severity}
                          </span>
                          {alert.tenant?.name && (
                            <span>· {alert.tenant.name}</span>
                          )}
                          <span>· {new Date(alert.createdAt).toLocaleString('pt-BR')}</span>
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
          </div>
        </PageLayout>
      </div>
    </div>
  );
}
